import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Spinner } from '~/components/Spinner';

import {
  exportDerivativeCSV,
  getDerivativePageData,
} from '../../api/derivative';
import { getLabelHierarchy } from '../../api/labels';
import type {
  ColDef,
  DataGridState,
  DataLoader,
  DataQuery,
  ExportCSVPayload,
  ExportCSVResponse,
} from '../../components/DataGrid/types';
import { ViewType } from '../../components/DataGrid/types/views';
import { arrayUnique, camelToSnake } from '../../components/DataGrid/utils';
import { convertFilters } from '../../components/DataGrid/utils/serialize';
import type { DerivativeRow, DerivativeRowKeyWithPrefix } from '../../types';
import { columnDefs } from './columns';
import { filterColumns } from './filter';

const LazyDerivativeGrid = lazy(() =>
  import('../../components/DataGrid').then((module) => ({
    default: module.DataGrid,
  })),
);

const defaultColDef: ColDef = {
  sortable: false,
  resizable: true,
};

export function Derivative() {
  const { t } = useTranslation('label-perf');
  const navigate = useNavigate();
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  const { data: labelHierarchyData, isLoading: isLoadingLabelsHierarchy } =
    useQuery({
      queryKey: ['labels-hierarchies-label-perf'],
      queryFn: () => getLabelHierarchy(),
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    });

  const filteredLabelHierarchyItems = useMemo(
    () => labelHierarchyData || [],
    [labelHierarchyData],
  );

  const downloadCSVFile = (url: string): void => {
    try {
      const downloadAnchor = document.createElement('a');

      downloadAnchor.style.display = 'none';
      downloadAnchor.href = url;
      downloadAnchor.download = 'CSV Export';
      downloadAnchor.click();
    } catch (err) {
      console.error('Error downloading CSV file:', err);
    }
  };

  const exportCSVFile = async (context: DataGridState): Promise<void> => {
    const { select, order, filter } = context;

    const payload: ExportCSVPayload = {
      select: arrayUnique(select.map((s) => camelToSnake(s))),
      order: order.map(
        (o) => `${o.direction === 'desc' ? '-' : ''}${camelToSnake(o.field)}`,
      ),
      filters: convertFilters(filter),
      search,
    };

    setExportLoading(true);

    const res = await exportDerivativeCSV(payload).catch((err) => {
      // eslint-disable-next-line no-console
      console.log('error', err);
      setExportLoading(false);
    });
    const url = (res as ExportCSVResponse)?.url;

    if (url) {
      downloadCSVFile(url);
      setExportLoading(false);
    }
  };

  const onExport = (context: DataGridState): void => {
    exportCSVFile(context).catch((err) => {
      // eslint-disable-next-line no-console
      console.log('error', err);
    });
  };

  const dataLoader: DataLoader<Partial<DerivativeRow>> = useCallback(
    (query: DataQuery<Partial<DerivativeRow>>) => {
      return getDerivativePageData({
        offset: query.nextPageToken
          ? Number(query.nextPageToken.split('=')[1])
          : 0,
        select: query.select,
        order: query.order?.map((s) =>
          s.direction === 'asc' ? s.field : `-${s.field}`,
        ) as DerivativeRowKeyWithPrefix[],
        filters: query.filters,
        search: query.search,
        wmg1_labels: query.wmg1_labels,
      });
    },
    [],
  );

  return (
    <>
      <Helmet>
        <title>
          {t('title.catalog')} - {t('title.derivative')}
        </title>
      </Helmet>
      <div className="flex h-[calc(100%-126px)] flex-wrap p-xl">
        <Suspense
          fallback={
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <Spinner size="md" />
            </div>
          }
        >
          <LazyDerivativeGrid
            columnDefs={columnDefs}
            dataLoader={dataLoader}
            defaultColDef={defaultColDef}
            enableConfig={true}
            enableExpand={true}
            enableExport={true}
            enableFilters={true}
            exportLoading={exportLoading}
            filterColumns={filterColumns}
            labelHierarchyData={filteredLabelHierarchyItems}
            labelsLoading={isLoadingLabelsHierarchy}
            id="derivative"
            limit={100}
            onExport={onExport}
            onSearch={setSearch}
            onViewChanged={(view) => {
              if (view === 'Derivative') {
                return;
              }

              if (view === 'Territories') {
                navigate('../territories');
              }

              if (view === 'TrackRollup') {
                navigate('../rollup');
              }
            }}
            viewIcon="microscope"
            views={[
              ViewType.Territories,
              ViewType.Derivative,
              ViewType.TrackRollup,
            ]}
          />
        </Suspense>
      </div>
    </>
  );
}

Derivative.displayName = 'Derivative';
